/**
 * @internal
 * Internal time utilities for TimePicker component.
 * These utilities are specific to the TimePicker implementation and should not be used externally.
 */

import type { Time, TimeFormat, ResolvedTimeFormat, TimeOption, MinuteStep } from './time.types.internal';

/** Total minutes in a day (24 hours * 60 minutes) */
const MINUTES_PER_DAY = 24 * 60;

/**
 * Cache for locale time format detection to avoid repeated Intl API calls.
 * Maps locale string to ResolvedTimeFormat ('12h' or '24h').
 */
const localeFormatCache = new Map<string, ResolvedTimeFormat>();

/**
 * Detect whether a locale uses 12-hour or 24-hour time format.
 *
 * Uses the Intl.DateTimeFormat API to format a test time and detect
 * the presence of AM/PM markers.
 *
 * @param locale - BCP 47 language tag (e.g., 'en-US', 'de-DE', 'ja-JP')
 * @returns '12h' for locales using AM/PM, '24h' for 24-hour locales
 *
 * @example
 * getLocaleTimeFormat('en-US') // '12h' (uses AM/PM)
 * getLocaleTimeFormat('de-DE') // '24h' (24-hour format)
 * getLocaleTimeFormat('fr-FR') // '24h' (24-hour format)
 * getLocaleTimeFormat('en-GB') // '24h' (British English uses 24h)
 *
 * @internal
 */
export function getLocaleTimeFormat(locale: string): ResolvedTimeFormat {
  // Check cache first
  const cached = localeFormatCache.get(locale);
  if (cached) {
    return cached;
  }

  try {
    // Create a date at 13:00 (1 PM) to test format
    const testDate = new Date(2000, 0, 1, 13, 0, 0);

    // Format with the locale's default time format
    const formatter = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: 'numeric',
    });

    // Use formatToParts to detect 12h vs 24h format
    // This correctly handles all locales, including those with non-English
    // day period markers (e.g., zh-TW "上午", ko-KR "오전", ja-JP "午前")
    const parts = formatter.formatToParts(testDate);
    const is12Hour = parts.some(part => part.type === 'dayPeriod');

    const result: ResolvedTimeFormat = is12Hour ? '12h' : '24h';

    // Cache the result
    localeFormatCache.set(locale, result);

    return result;
  } catch {
    // Fallback to 12h if Intl API fails (shouldn't happen in modern browsers)
    return '12h';
  }
}

/**
 * Resolve a TimeFormat to a concrete format ('12h' or '24h').
 *
 * If the format is 'auto', it uses the locale to determine the appropriate format.
 * If the format is already '12h' or '24h', it returns it unchanged.
 *
 * @param format - The format to resolve ('12h', '24h', or 'auto')
 * @param locale - The locale to use for 'auto' resolution (defaults to 'en-US')
 * @returns The resolved format ('12h' or '24h')
 *
 * @example
 * resolveTimeFormat('12h', 'de-DE') // '12h' (explicit format preserved)
 * resolveTimeFormat('auto', 'en-US') // '12h' (US uses 12-hour)
 * resolveTimeFormat('auto', 'de-DE') // '24h' (Germany uses 24-hour)
 *
 * @internal
 */
export function resolveTimeFormat(format: TimeFormat, locale: string = 'en-US'): ResolvedTimeFormat {
  if (format === 'auto') {
    return getLocaleTimeFormat(locale);
  }
  return format;
}

/**
 * Parse a freeform time input string into a Time object.
 *
 * Supports various formats:
 * - "10", "10p", "10pm", "10 pm" → 10:00 AM/PM
 * - "1012p", "1012pm" → 10:12 PM
 * - "22", "2212" → 22:00, 22:12 (24h format)
 * - "12a" → 12:00 AM (midnight)
 * - "12p" → 12:00 PM (noon)
 * - "10:30", "10:30pm" → colon-separated formats
 *
 * @param input - The user input string
 * @param _format - Time format (optional, reserved for future use). Must be a resolved format ('12h' or '24h').
 * @returns Time object or undefined if invalid
 *
 * @internal
 */
export function parseTimeInput(input: string, _format: ResolvedTimeFormat = '12h'): Time | undefined {
  if (!input || typeof input !== 'string') {
    return undefined;
  }

  // Normalize input: trim, lowercase, remove spaces
  const normalized = input.trim().toLowerCase().replace(/\s+/g, '');

  if (normalized === '') {
    return undefined;
  }

  // Check for AM/PM suffix
  let meridiem: 'am' | 'pm' | null = null;
  let timeString = normalized;

  if (normalized.endsWith('am') || normalized.endsWith('a')) {
    meridiem = 'am';
    timeString = normalized.replace(/a(m)?$/, '');
  } else if (normalized.endsWith('pm') || normalized.endsWith('p')) {
    meridiem = 'pm';
    timeString = normalized.replace(/p(m)?$/, '');
  }

  // Handle colon-separated format (e.g., "10:30", "10:30pm")
  if (timeString.includes(':')) {
    const parts = timeString.split(':');
    if (parts.length !== 2) return undefined;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (isNaN(hours) || isNaN(minutes)) return undefined;

    return normalizeTime(hours, minutes, meridiem);
  }

  // Handle numeric-only format
  const numericOnly = timeString.replace(/[^0-9]/g, '');

  if (numericOnly === '') {
    return undefined;
  }

  let hours: number;
  let minutes: number;

  const len = numericOnly.length;

  if (len === 1 || len === 2) {
    // "9", "10", "22" - hours only
    hours = parseInt(numericOnly, 10);
    minutes = 0;
  } else if (len === 3) {
    // "912" → 9:12, "000" → 0:00
    hours = parseInt(numericOnly.slice(0, 1), 10);
    minutes = parseInt(numericOnly.slice(1), 10);
  } else if (len === 4) {
    // "1012" → 10:12, "2212" → 22:12
    hours = parseInt(numericOnly.slice(0, 2), 10);
    minutes = parseInt(numericOnly.slice(2), 10);
  } else {
    // More than 4 digits is invalid
    return undefined;
  }

  return normalizeTime(hours, minutes, meridiem);
}

/**
 * Normalize hours and minutes, applying meridiem conversion if needed.
 *
 * @internal
 */
function normalizeTime(hours: number, minutes: number, meridiem: 'am' | 'pm' | null): Time | undefined {
  // Validate minutes
  if (minutes < 0 || minutes > 59) {
    return undefined;
  }

  // If meridiem is specified, validate and convert
  if (meridiem !== null) {
    // With meridiem, hours must be 1-12
    if (hours < 1 || hours > 12) {
      return undefined;
    }

    // Convert 12h to 24h
    if (meridiem === 'am') {
      // 12 AM = 0, 1-11 AM = 1-11
      hours = hours === 12 ? 0 : hours;
    } else {
      // 12 PM = 12, 1-11 PM = 13-23
      hours = hours === 12 ? 12 : hours + 12;
    }
  } else {
    // No meridiem specified - accept 24h input (0-23)
    if (hours < 0 || hours > 23) {
      return undefined;
    }
  }

  return { hours, minutes };
}

/**
 * Cache for Intl.DateTimeFormat instances to avoid repeated instantiation.
 * Key format: `${locale}-${format}` (e.g., 'en-US-12h', 'de-DE-24h')
 */
const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * Get or create a cached DateTimeFormat instance for time formatting.
 *
 * @internal
 */
function getTimeFormatter(locale: string, format: ResolvedTimeFormat): Intl.DateTimeFormat {
  const cacheKey = `${locale}-${format}`;
  let formatter = timeFormatterCache.get(cacheKey);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: format === '12h',
    });
    timeFormatterCache.set(cacheKey, formatter);
  }

  return formatter;
}

/**
 * Format a Time object as a display string.
 *
 * For 12h format, uses Intl.DateTimeFormat for locale-aware formatting, which properly handles
 * localized day-period markers (e.g., AM/PM in English, 上午/下午 in Chinese,
 * 오전/오후 in Korean).
 *
 * For 24h format, uses simple HH:MM padding (no locale variation needed).
 *
 * @param time - The time to format
 * @param format - Display format ('12h' or '24h'). Must be a resolved format.
 * @param locale - BCP 47 language tag for localized output (defaults to 'en-US')
 * @returns Formatted time string (e.g., "10:30 PM", "22:30", "下午 10:30")
 *
 * @example
 * formatTime({ hours: 22, minutes: 30 }, '12h', 'en-US')  // "10:30 PM"
 * formatTime({ hours: 22, minutes: 30 }, '12h', 'zh-TW')  // "下午10:30"
 * formatTime({ hours: 22, minutes: 30 }, '24h', 'de-DE')  // "22:30"
 *
 * @internal
 */
export function formatTime(time: Time, format: ResolvedTimeFormat, locale: string = 'en-US'): string {
  const { hours, minutes } = time;

  // For 24h format, use simple padding - no locale-specific variation needed
  // This avoids the Intl quirk where some implementations produce "24:00" for midnight
  if (format === '24h') {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // For 12h format, use Intl.DateTimeFormat for locale-aware formatting
  // (handles AM/PM vs 上午/下午 vs 오전/오후 etc.)
  const date = new Date(2000, 0, 1, hours, minutes, 0);

  return getTimeFormatter(locale, format).format(date);
}

/**
 * Generate all time options for a dropdown.
 *
 * @param format - Display format for labels. Must be a resolved format ('12h' or '24h').
 * @param step - Minute increment (15, 30, or 60)
 * @param locale - BCP 47 language tag for localized labels (defaults to 'en-US')
 * @returns Array of time options with value and label
 *
 * @internal
 */
export function generateTimeOptions(format: ResolvedTimeFormat, step: MinuteStep = 15, locale: string = 'en-US'): TimeOption[] {
  const options: TimeOption[] = [];

  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += step) {
      const time: Time = { hours, minutes };
      options.push({
        value: time,
        label: formatTime(time, format, locale),
      });
    }
  }

  return options;
}

/**
 * Compare two Time objects for equality.
 *
 * @param a - First time (can be undefined)
 * @param b - Second time (can be undefined)
 * @returns True if both are equal or both are undefined
 *
 * @internal
 */
export function isTimeEqual(a: Time | undefined, b: Time | undefined): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a.hours === b.hours && a.minutes === b.minutes;
}

/**
 * Convert a Time object to total minutes since midnight.
 *
 * @param time - The time to convert
 * @returns Total minutes (0-1439)
 *
 * @internal
 */
export function timeToMinutes(time: Time): number {
  return time.hours * 60 + time.minutes;
}

/**
 * Find the closest time option to a given time.
 *
 * @param time - The target time
 * @param options - Array of time options
 * @returns The closest option, or undefined if no options
 *
 * @internal
 */
export function findClosestTimeOption(time: Time, options: TimeOption[]): TimeOption | undefined {
  if (options.length === 0) return undefined;

  const targetMinutes = timeToMinutes(time);
  let closest = options[0];
  let minDiff = Math.abs(timeToMinutes(closest.value) - targetMinutes);

  for (const option of options) {
    const diff = Math.abs(timeToMinutes(option.value) - targetMinutes);
    if (diff < minDiff) {
      minDiff = diff;
      closest = option;
    }
  }

  return closest;
}

/**
 * Increment time by a given step in minutes, wrapping at day boundaries.
 *
 * @param time - The current time (or undefined for no selection)
 * @param step - Minutes to increment by (15, 30, or 60 recommended)
 * @returns The new time after incrementing
 *
 * @internal
 */
export function incrementTime(time: Time | undefined, step: MinuteStep): Time {
  if (!time) {
    // If no time selected, start at midnight
    return { hours: 0, minutes: 0 };
  }

  let totalMinutes = timeToMinutes(time) + step;

  // Wrap around at end of day
  if (totalMinutes >= MINUTES_PER_DAY) {
    totalMinutes = totalMinutes % MINUTES_PER_DAY;
  }

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

/**
 * Decrement time by a given step in minutes, wrapping at day boundaries.
 *
 * @param time - The current time (or undefined for no selection)
 * @param step - Minutes to decrement by (15, 30, or 60 recommended)
 * @returns The new time after decrementing
 *
 * @internal
 */
export function decrementTime(time: Time | undefined, step: MinuteStep): Time {
  if (!time) {
    // If no time selected, start at end of day (last valid time option)
    const lastMinutes = MINUTES_PER_DAY - step;
    return {
      hours: Math.floor(lastMinutes / 60),
      minutes: lastMinutes % 60,
    };
  }

  let totalMinutes = timeToMinutes(time) - step;

  // Wrap around at start of day
  if (totalMinutes < 0) {
    totalMinutes = MINUTES_PER_DAY + totalMinutes;
  }

  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}

/**
 * Snap a time to the nearest valid option based on step.
 *
 * @param time - The time to snap
 * @param step - The minute step (15, 30, or 60)
 * @returns The snapped time
 *
 * @internal
 */
export function snapToStep(time: Time, step: MinuteStep): Time {
  const totalMinutes = timeToMinutes(time);
  const snappedMinutes = Math.round(totalMinutes / step) * step;

  // Handle wrap-around
  const normalizedMinutes = snappedMinutes >= MINUTES_PER_DAY ? 0 : snappedMinutes;

  return {
    hours: Math.floor(normalizedMinutes / 60),
    minutes: normalizedMinutes % 60,
  };
}

/**
 * Step forward to the next valid time option.
 *
 * If the current time is on a step boundary, moves to the next step.
 * If the current time is between steps, moves to the next step boundary.
 *
 * @example
 * // With 15-minute steps:
 * stepForward({ hours: 10, minutes: 0 }, 15)  // → 10:15 (on boundary, move to next)
 * stepForward({ hours: 10, minutes: 10 }, 15) // → 10:15 (between steps, snap forward)
 * stepForward({ hours: 10, minutes: 7 }, 15)  // → 10:15 (between steps, snap forward)
 *
 * @param time - The current time (or undefined for no selection)
 * @param step - Minutes per step (15, 30, or 60 recommended)
 * @returns The next valid time on the step grid
 *
 * @internal
 */
export function stepForward(time: Time | undefined, step: MinuteStep): Time {
  if (!time) {
    // If no time selected, start at midnight
    return { hours: 0, minutes: 0 };
  }

  const totalMinutes = timeToMinutes(time);
  // Floor to current/previous boundary, then add step to get next boundary
  const nextMinutes = Math.floor(totalMinutes / step) * step + step;

  // Wrap around at end of day
  const normalizedMinutes = nextMinutes >= MINUTES_PER_DAY ? nextMinutes % MINUTES_PER_DAY : nextMinutes;

  return {
    hours: Math.floor(normalizedMinutes / 60),
    minutes: normalizedMinutes % 60,
  };
}

/**
 * Step backward to the previous valid time option.
 *
 * If the current time is on a step boundary, moves to the previous step.
 * If the current time is between steps, moves to the previous step boundary.
 *
 * @example
 * // With 15-minute steps:
 * stepBackward({ hours: 10, minutes: 15 }, 15) // → 10:00 (on boundary, move to prev)
 * stepBackward({ hours: 10, minutes: 10 }, 15) // → 10:00 (between steps, snap backward)
 * stepBackward({ hours: 10, minutes: 7 }, 15)  // → 10:00 (between steps, snap backward)
 *
 * @param time - The current time (or undefined for no selection)
 * @param step - Minutes per step (15, 30, or 60 recommended)
 * @returns The previous valid time on the step grid
 *
 * @internal
 */
export function stepBackward(time: Time | undefined, step: MinuteStep): Time {
  if (!time) {
    // If no time selected, start at end of day (last valid time option)
    const lastMinutes = MINUTES_PER_DAY - step;
    return {
      hours: Math.floor(lastMinutes / 60),
      minutes: lastMinutes % 60,
    };
  }

  const totalMinutes = timeToMinutes(time);
  // Ceil to current/next boundary, then subtract step to get previous boundary
  const prevMinutes = Math.ceil(totalMinutes / step) * step - step;

  // Wrap around at start of day
  const normalizedMinutes = prevMinutes < 0 ? MINUTES_PER_DAY + prevMinutes : prevMinutes;

  return {
    hours: Math.floor(normalizedMinutes / 60),
    minutes: normalizedMinutes % 60,
  };
}

/**
 * Check if a time falls within the specified range (inclusive).
 *
 * @param time - The time to check
 * @param min - Optional minimum time (inclusive)
 * @param max - Optional maximum time (inclusive)
 * @returns True if time is within range, false otherwise
 *
 * @internal
 */
export function isTimeInRange(time: Time, min?: Time, max?: Time): boolean {
  const timeMinutes = timeToMinutes(time);

  if (min !== undefined) {
    const minMinutes = timeToMinutes(min);
    if (timeMinutes < minMinutes) {
      return false;
    }
  }

  if (max !== undefined) {
    const maxMinutes = timeToMinutes(max);
    if (timeMinutes > maxMinutes) {
      return false;
    }
  }

  return true;
}

/**
 * Filter time options to only include those within the specified range.
 *
 * @param options - Array of time options to filter
 * @param min - Optional minimum time (inclusive)
 * @param max - Optional maximum time (inclusive)
 * @returns Filtered array of time options within range
 *
 * @internal
 */
export function filterTimeOptions(options: TimeOption[], min?: Time, max?: Time): TimeOption[] {
  if (min === undefined && max === undefined) {
    return options;
  }

  return options.filter(option => isTimeInRange(option.value, min, max));
}

/**
 * Convert a Time object to native input[type="time"] value format (HH:MM).
 *
 * @param time - The time to convert (can be undefined)
 * @returns String in HH:MM format, or empty string if undefined
 *
 * @internal
 */
export function timeToInputValue(time: Time | undefined): string {
  if (!time) return '';
  const hours = time.hours.toString().padStart(2, '0');
  const minutes = time.minutes.toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Parse a native input[type="time"] value (HH:MM) to a Time object.
 *
 * @param value - The input value in HH:MM format
 * @returns Time object or undefined if empty/invalid
 *
 * @internal
 */
export function inputValueToTime(value: string): Time | undefined {
  if (!value) return undefined;

  const parts = value.split(':');
  if (parts.length < 2) return undefined;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) return undefined;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return undefined;

  return { hours, minutes };
}

/**
 * Parse a time string in "HH:mm" format to a Time object.
 *
 * Attempts to parse the string - returns undefined if invalid.
 * The TimePicker's existing validation will show "Invalid time format" error.
 *
 * @param value - Time string in "HH:mm" format (e.g., "09:30", "14:00")
 * @returns Time object or undefined if invalid/empty
 *
 * @example
 * stringToTime("09:30")  // { hours: 9, minutes: 30 }
 * stringToTime("14:00")  // { hours: 14, minutes: 0 }
 * stringToTime("")       // undefined
 * stringToTime("invalid") // undefined (existing error handling kicks in)
 *
 * @internal
 */
export function stringToTime(value: string): Readonly<Time> | undefined {
  if (!value) return undefined;
  const [hours, minutes] = value.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return undefined;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return undefined;
  return { hours, minutes };
}

/**
 * Format a Time object to a string in "HH:mm" format.
 *
 * @param time - Time object to format
 * @returns Time string in "HH:mm" format, or empty string if undefined
 *
 * @example
 * timeToString({ hours: 9, minutes: 30 })  // "09:30"
 * timeToString({ hours: 14, minutes: 0 })  // "14:00"
 * timeToString(undefined)                   // ""
 *
 * @internal
 */
export function timeToString(time: Readonly<Time> | undefined): string {
  if (!time) return '';
  return `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}`;
}
